/**
 * Image comparison of the effects comparer (W3-04; Appendix C H5): Phiedit's render of an effect against Phira's
 * capture of the same frame. Colour channels only (captures are opaque).
 *
 *  - `mean`: mean absolute difference per channel (0..255);
 *  - `psnr`: peak signal-to-noise ratio in dB over the colour channels (Infinity when identical);
 *  - `over`: fraction of pixels whose largest channel difference exceeds `threshold`;
 *  - `max`: the largest channel difference.
 *
 * The heat map shows the reference dimmed to a third, with every pixel over the threshold painted from `low` (just
 * over) to `high` (a difference of 128 or more), so differences stand out while the picture stays readable.
 */

import type { RgbaImage } from './bench';

export interface DiffStats {
  readonly mean: number;
  readonly psnr: number;
  readonly over: number;
  readonly max: number;
}

export type Rgb = readonly [number, number, number];

export interface DiffOptions {
  /** Channel difference a pixel must exceed to count (and be painted) as different. */
  readonly threshold?: number;
  readonly low: Rgb;
  readonly high: Rgb;
}

export const DEFAULT_THRESHOLD = 8;

/** Compares two images of the same size. */
export function diffImages(a: RgbaImage, b: RgbaImage, opts: DiffOptions): { stats: DiffStats; heat: RgbaImage } {
  if (a.width !== b.width || a.height !== b.height) {
    throw new Error(`diffImages: sizes differ (${a.width}x${a.height} vs ${b.width}x${b.height})`);
  }
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  const n = a.width * a.height;
  const heat = new Uint8ClampedArray(n * 4);
  let sum = 0;
  let sq = 0;
  let over = 0;
  let max = 0;
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    let worst = 0;
    for (let k = 0; k < 3; k++) {
      const d = Math.abs((a.data[i + k] ?? 0) - (b.data[i + k] ?? 0));
      sum += d;
      sq += d * d;
      if (d > worst) worst = d;
    }
    if (worst > max) max = worst;
    if (worst > threshold) {
      over++;
      const t = Math.min(1, (worst - threshold) / Math.max(1, 128 - threshold));
      for (let k = 0; k < 3; k++) heat[i + k] = (opts.low[k] ?? 0) + ((opts.high[k] ?? 0) - (opts.low[k] ?? 0)) * t;
    } else {
      for (let k = 0; k < 3; k++) heat[i + k] = (b.data[i + k] ?? 0) / 3;
    }
    heat[i + 3] = 255;
  }
  const mse = sq / Math.max(1, n * 3);
  return {
    stats: {
      mean: sum / Math.max(1, n * 3),
      psnr: mse === 0 ? Infinity : 10 * Math.log10((255 * 255) / mse),
      over: n === 0 ? 0 : over / n,
      max,
    },
    heat: { width: a.width, height: a.height, data: heat },
  };
}

/** Parses a CSS colour of the form `#rgb`, `#rrggbb` or `rgb(r g b ...)` / `rgb(r, g, b)`; null otherwise. */
export function parseCssRgb(text: string): Rgb | null {
  const s = text.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex !== null) {
    const h = hex[1] ?? '';
    const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
    return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(s);
  if (fn !== null) return [Number(fn[1]), Number(fn[2]), Number(fn[3])];
  return null;
}
